import React, { useState, useEffect } from "react";
import { auth, db, googleProvider } from "../firebase-config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  linkWithCredential,
  linkWithPopup,
  signInWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { INDIAN_STATES, SUPPORT_WHATSAPP } from "../utils/enums";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const GoogleMark = () => (
  <Box
    component="svg"
    viewBox="0 0 48 48"
    aria-hidden="true"
    sx={{ width: 20, height: 20 }}
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </Box>
);

// A guest (anonymous) session is upgraded in place so their guest exam results keep the same uid.
const isGuestSession = () => Boolean(auth.currentUser?.isAnonymous);

// The sign-up form writes every field at once, so `class` present means the form was completed.
export const isProfileComplete = (profile) => Boolean(profile?.class);

// Email/password login and registration are switched off for now; Google sign-in only. Flip to bring them back.
const EMAIL_AUTH_ENABLED = false;

function Login({
  onLoginComplete,
  reason,
  reasonCode,
  onContinueAsGuest,
  incompleteProfileUser,
  onProfileSaved,
}) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "",
    fatherName: "",
    email: "",
    phone: "",
    classLevel: "",
    combination: "",
    state: "",
    password: "",
  });

  const [showGooglePrompt, setShowGooglePrompt] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [googleExtraData, setGoogleExtraData] = useState({
    name: "",
    fatherName: "",
    phone: "",
    classLevel: "",
    combination: "",
    state: "",
  });

  useEffect(() => {
    // `loading`: email sign-up creates the account a moment before saving the profile
    if (incompleteProfileUser && !loading) {
      setGoogleUser(incompleteProfileUser);
      setGoogleExtraData((d) => ({
        ...d,
        name: d.name || incompleteProfileUser.displayName || "",
      }));
      setShowGooglePrompt(true);
    }
  }, [incompleteProfileUser, loading]);

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setRegisterData({ ...registerData, [name]: cleaned });
    } else {
      setRegisterData({ ...registerData, [name]: value });
    }
  };

  const handleGoogleExtraChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setGoogleExtraData({ ...googleExtraData, [name]: cleaned });
    } else {
      setGoogleExtraData({ ...googleExtraData, [name]: value });
    }
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (registerData.phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = isGuestSession()
        ? await linkWithCredential(
            auth.currentUser,
            EmailAuthProvider.credential(
              registerData.email,
              registerData.password
            )
          )
        : await createUserWithEmailAndPassword(
            auth,
            registerData.email,
            registerData.password
          );
      const user = userCredential.user;

      const userData = {
        authProvider: "EMAIL",
        class: registerData.classLevel,
        combination: registerData.combination,
        createdAt: getTodayFormatted(),
        email: registerData.email,
        name: registerData.name,
        fatherName: registerData.fatherName.trim(),
        phone: registerData.phone,
        state: registerData.state,
      };

      await setDoc(doc(db, "users", user.uid), userData);
      await onProfileSaved?.();
      await sendEmailVerification(user);

      setMessage(
        "Account created successfully! Please check your email to verify your account before logging in."
      );
      setIsLogin(true);
      setRegisterData({
        name: "",
        fatherName: "",
        email: "",
        phone: "",
        classLevel: "",
        combination: "",
        state: "",
        password: "",
      });
    } catch (err) {
      console.error("Registration Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        setError(
          "Please verify your email address before logging in. Check your inbox."
        );
        auth.signOut();
        return;
      }
      onLoginComplete();
    } catch (err) {
      console.error("Login Error:", err);
      setError("Invalid credentials or account does not exist.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      let result;
      if (isGuestSession()) {
        try {
          result = await linkWithPopup(auth.currentUser, googleProvider);
        } catch (linkErr) {
          // Google account already exists: sign into it instead (the guest result stays with the guest id)
          if (linkErr.code !== "auth/credential-already-in-use") throw linkErr;
          result = await signInWithCredential(
            auth,
            GoogleAuthProvider.credentialFromError(linkErr)
          );
        }
      } else {
        result = await signInWithPopup(auth, googleProvider);
      }
      const user = result.user;

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (isProfileComplete(docSnap.data())) {
        onLoginComplete();
      } else {
        setGoogleUser(user);
        setGoogleExtraData((d) => ({
          ...d,
          name: d.name || user.displayName || "",
        }));
        setShowGooglePrompt(true);
      }
    } catch (err) {
      console.error("Google Sign In Error:", err);
      setError("Google Sign-In failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleExtraSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (googleExtraData.phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      setLoading(false);
      return;
    }

    try {
      const userData = {
        authProvider: googleUser.providerData?.some(
          (p) => p.providerId === "google.com"
        )
          ? "GMAIL"
          : "EMAIL",
        class: googleExtraData.classLevel,
        combination: googleExtraData.combination,
        createdAt: getTodayFormatted(),
        email: googleUser.email,
        name: googleExtraData.name.trim(),
        phone: googleExtraData.phone,
        fatherName: googleExtraData.fatherName.trim(),
        state: googleExtraData.state,
      };

      await setDoc(doc(db, "users", googleUser.uid), userData);

      setShowGooglePrompt(false);
      onLoginComplete();
    } catch (err) {
      console.error("Error saving Google profile:", err);
      setError("Failed to complete Google registration.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      fontSize: "11px",
      color: "#333333",
      borderRadius: "4px",
      bgcolor: "#ffffff",
      "& fieldset": { borderColor: "#e8e8e8" },
      "&:hover fieldset": { borderColor: "#b0b0b0" },
      "&.Mui-focused fieldset": { borderColor: "#1a204d" },
    },
    "& .MuiInputBase-input": { padding: "7px 10px" },
    "& .MuiInputLabel-root": { fontSize: "11px", top: -5 },
  };

  const btnStyle = {
    bgcolor: "#f2722b",
    color: "#ffffff",
    py: "8px",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { bgcolor: "#e0661a", boxShadow: "none" },
    "&.Mui-disabled": { bgcolor: "#9ca3af", color: "#ffffff" },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(380px, 46%) 1fr" },
        gridTemplateRows: { xs: "auto 1fr", md: "1fr" },
        bgcolor: "#ffffff",
      }}
    >
      {/* Brand side: what students get. On phones it shrinks to a compact header. */}
      <Box
        component="aside"
        sx={{
          bgcolor: "#1a204d",
          color: "#ffffff",
          px: { xs: "20px", sm: "32px", md: "56px" },
          py: { xs: "18px", md: "48px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: { xs: 0, md: "40px" },
        }}
      >
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderRadius: "12px",
            px: "10px",
            width: "fit-content",
            display: "flex",
          }}
        >
          <Box
            component="img"
            src="/educonnect_logo.jpg"
            alt="Educonnect"
            sx={{ height: { xs: "40px", md: "52px" }, display: "block" }}
          />
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" }, maxWidth: "440px" }}>
          <Typography
            component="h2"
            sx={{
              fontSize: { md: "36px", lg: "42px" },
              fontWeight: 700,
              lineHeight: 1.12,
              letterSpacing: "-0.03em",
              textWrap: "balance",
            }}
          >
            Practise for KCET, JEE and NEET{" "}
            <Box component="span" sx={{ color: "#f97316" }}>
              the way the real exam feels.
            </Box>
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
              mt: "32px",
              display: "grid",
              gap: "18px",
            }}
          >
            {[
              [
                "Exam-pattern tests",
                "Each exam type's own marking and difficulty mix.",
              ],
              [
                "Chapter or topic practice",
                "Pick exactly what you want to revise.",
              ],
              [
                "Score with full solutions",
                "See every answer explained the moment you submit.",
              ],
            ].map(([title, text]) => (
              <Box
                component="li"
                key={title}
                sx={{ display: "flex", gap: "14px" }}
              >
                <CheckRoundedIcon
                  sx={{
                    color: "#f97316",
                    fontSize: 22,
                    mt: "1px",
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#c7cbe0",
                      mt: "2px",
                      lineHeight: 1.5,
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: "13px",
            color: "#9aa0c3",
          }}
        >
          freemocktest.net
        </Typography>
      </Box>

      {/* Sign-in side */}
      <Box
        component="main"
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "center",
          px: { xs: "20px", sm: "32px" },
          py: { xs: "36px", md: "48px" },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "400px" }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "26px", md: "30px" },
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {!EMAIL_AUTH_ENABLED
              ? "Sign in to Educonnect"
              : isLogin
              ? "Login"
              : "Register"}
          </Typography>
          <Typography
            sx={{
              fontSize: "15px",
              color: "#64748b",
              mt: "8px",
              mb: "28px",
              lineHeight: 1.55,
            }}
          >
            Use your Google account. It takes a few seconds and keeps your
            scores and solutions saved.
          </Typography>

          {reason === "guest-unavailable" && !error && !message && (
            <Alert
              severity="warning"
              sx={{
                mb: "20px",
                borderRadius: "12px",
                "& .MuiAlert-message": { fontSize: "14px", lineHeight: 1.5 },
              }}
            >
              Guest access isn't available right now, please sign in.
              {reasonCode ? ` (${reasonCode})` : ""}
            </Alert>
          )}
          {reason === "results" && !error && !message && (
            <Alert
              severity="info"
              sx={{
                mb: "20px",
                borderRadius: "12px",
                "& .MuiAlert-message": { fontSize: "14px", lineHeight: 1.5 },
              }}
            >
              Your exam is submitted. Sign in or create a free account to see
              your results.
            </Alert>
          )}
          {reason === "limit" && !error && !message && (
            <Alert
              severity="info"
              sx={{
                mb: "20px",
                borderRadius: "12px",
                "& .MuiAlert-message": { fontSize: "14px", lineHeight: 1.5 },
              }}
            >
              You've used your free exam. Sign in or create an account to keep
              practising and save your results.
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: "20px",
                borderRadius: "12px",
                "& .MuiAlert-message": { fontSize: "14px", lineHeight: 1.5 },
              }}
            >
              {error}
            </Alert>
          )}
          {message && (
            <Alert
              severity="success"
              sx={{
                mb: "20px",
                borderRadius: "12px",
                "& .MuiAlert-message": { fontSize: "14px", lineHeight: 1.5 },
              }}
            >
              {message}
            </Alert>
          )}

          {EMAIL_AUTH_ENABLED && (
            <>
              {isLogin ? (
                <Box
                  component="form"
                  onSubmit={handleLoginSubmit}
                  sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
                >
                  <TextField
                    fullWidth
                    placeholder="Email Address"
                    name="email"
                    type="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    required
                    sx={inputStyle}
                  />
                  <TextField
                    fullWidth
                    placeholder="Password"
                    name="password"
                    type="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    required
                    sx={inputStyle}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{ ...btnStyle, mt: "4px" }}
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleRegisterSubmit}
                  sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  <TextField
                    fullWidth
                    placeholder="Student Name"
                    name="name"
                    value={registerData.name}
                    onChange={handleRegisterChange}
                    required
                    sx={inputStyle}
                  />
                  <TextField
                    fullWidth
                    placeholder="Father Name"
                    name="fatherName"
                    value={registerData.fatherName}
                    onChange={handleRegisterChange}
                    required
                    sx={inputStyle}
                  />
                  <TextField
                    fullWidth
                    placeholder="Email Address"
                    name="email"
                    type="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    required
                    sx={inputStyle}
                  />
                  <TextField
                    fullWidth
                    placeholder="Create a Password (min 6 chars)"
                    name="password"
                    type="password"
                    inputProps={{ minLength: 6 }}
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    required
                    sx={inputStyle}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Phone Number"
                      name="phone"
                      type="tel"
                      value={registerData.phone}
                      onChange={handleRegisterChange}
                      required
                      sx={inputStyle}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <TextField
                      fullWidth
                      select
                      value={registerData.classLevel || ""}
                      name="classLevel"
                      onChange={handleRegisterChange}
                      required
                      sx={inputStyle}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          sx: { "& .MuiMenuItem-root": { fontSize: "11px" } },
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                        Select Class
                      </MenuItem>
                      <MenuItem value="1st PUC" sx={{ fontSize: "11px" }}>
                        1st PUC
                      </MenuItem>
                      <MenuItem value="2nd PUC" sx={{ fontSize: "11px" }}>
                        2nd PUC
                      </MenuItem>
                      <MenuItem value="Long Term" sx={{ fontSize: "11px" }}>
                        Long Term
                      </MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      value={registerData.combination || ""}
                      name="combination"
                      onChange={handleRegisterChange}
                      required
                      sx={inputStyle}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          sx: { "& .MuiMenuItem-root": { fontSize: "11px" } },
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                        Combination
                      </MenuItem>
                      <MenuItem value="PCMB" sx={{ fontSize: "11px" }}>
                        PCMB
                      </MenuItem>
                      <MenuItem value="PCMC" sx={{ fontSize: "11px" }}>
                        PCMC
                      </MenuItem>
                    </TextField>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    value={registerData.state || ""}
                    name="state"
                    onChange={handleRegisterChange}
                    required
                    sx={inputStyle}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        sx: { "& .MuiMenuItem-root": { fontSize: "11px" } },
                        PaperProps: { sx: { maxHeight: 280 } },
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                      Select State
                    </MenuItem>
                    {INDIAN_STATES.map((st) => (
                      <MenuItem key={st} value={st} sx={{ fontSize: "11px" }}>
                        {st}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{ ...btnStyle, mt: "2px" }}
                  >
                    {loading ? "Processing..." : "Create my account"}
                  </Button>
                </Box>
              )}

              <Typography
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError(null);
                  setMessage(null);
                }}
                sx={{
                  mt: "8px",
                  mb: "2px",
                  fontSize: "11px",
                  color: "#000000",
                  fontWeight: 600,
                  textAlign: "center",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {isLogin
                  ? "Don't have an account? Register"
                  : "Already have an account? Login"}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", my: "8px" }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography
                  sx={{
                    mx: "16px",
                    color: "#999999",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>
            </>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={loading ? null : <GoogleMark />}
            sx={{
              height: "52px",
              borderRadius: "12px",
              borderColor: "#dadce0",
              color: "#1f1f1f",
              bgcolor: "#ffffff",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 600,
              "& .MuiButton-startIcon": { mr: "12px" },
              "&:hover": { bgcolor: "#f8fafc", borderColor: "#c4c7cc" },
              "&:focus-visible": {
                outline: "3px solid rgba(29, 78, 216, 0.35)",
                outlineOffset: "2px",
              },
            }}
          >
            {loading ? "Signing you in…" : "Continue with Google"}
          </Button>

          {onContinueAsGuest && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  my: "20px",
                }}
              >
                <Divider sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>
                  or
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
              <Button
                fullWidth
                onClick={onContinueAsGuest}
                disabled={loading}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  height: "48px",
                  borderRadius: "12px",
                  color: "#1d4ed8",
                  bgcolor: "#eff6ff",
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#dbeafe" },
                }}
              >
                Try exam as a guest
              </Button>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#94a3b8",
                  textAlign: "center",
                  mt: "10px",
                }}
              >
                No account needed. Sign in afterwards to see your results.
              </Typography>
            </>
          )}

          <Box
            component="a"
            href={`https://wa.me/91${SUPPORT_WHATSAPP}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: "36px",
              pt: "20px",
              borderTop: "1px solid #eef2f7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "13px",
              color: "#64748b",
              textDecoration: "none",
              "&:hover": { color: "#1a204d" },
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} />
            Need help? WhatsApp us at +91 {SUPPORT_WHATSAPP.slice(0, 5)}{" "}
            {SUPPORT_WHATSAPP.slice(5)}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={showGooglePrompt}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
          Almost there!
        </DialogTitle>
        <DialogContent sx={{ pb: "8px" }}>
          <Typography sx={{ fontSize: "13px", mb: "16px", color: "#666666" }}>
            We just need a few more details to set up your account.
          </Typography>
          <Box
            component="form"
            id="google-extra-form"
            onSubmit={handleGoogleExtraSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <TextField
              fullWidth
              placeholder="Student Name"
              name="name"
              value={googleExtraData.name}
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              sx={inputStyle}
            />
            <TextField
              fullWidth
              placeholder="Father Name"
              name="fatherName"
              value={googleExtraData.fatherName}
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              sx={inputStyle}
            />
            <TextField
              fullWidth
              placeholder="Phone Number"
              name="phone"
              type="tel"
              value={googleExtraData.phone}
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              sx={inputStyle}
              inputProps={{ maxLength: 10 }}
            />

            <TextField
              fullWidth
              select
              value={googleExtraData.classLevel || ""}
              name="classLevel"
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              SelectProps={{ displayEmpty: true }}
              sx={inputStyle}
            >
              <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                Select Class
              </MenuItem>
              <MenuItem value="1st PUC" sx={{ fontSize: "11px" }}>
                1st PUC
              </MenuItem>
              <MenuItem value="2nd PUC" sx={{ fontSize: "11px" }}>
                2nd PUC
              </MenuItem>
              <MenuItem value="Long Term" sx={{ fontSize: "11px" }}>
                Long Term
              </MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              value={googleExtraData.combination || ""}
              name="combination"
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              SelectProps={{ displayEmpty: true }}
              sx={inputStyle}
            >
              <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                Combination
              </MenuItem>
              <MenuItem value="PCMB" sx={{ fontSize: "11px" }}>
                PCMB
              </MenuItem>
              <MenuItem value="PCMC" sx={{ fontSize: "11px" }}>
                PCMC
              </MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              value={googleExtraData.state || ""}
              name="state"
              onChange={handleGoogleExtraChange}
              required
              variant="outlined"
              size="small"
              sx={inputStyle}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  sx: { "& .MuiMenuItem-root": { fontSize: "11px" } },
                  PaperProps: { sx: { maxHeight: 280 } },
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: "11px" }}>
                Select State
              </MenuItem>
              {INDIAN_STATES.map((st) => (
                <MenuItem key={st} value={st} sx={{ fontSize: "11px" }}>
                  {st}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: "24px", pb: "16px" }}>
          <Button
            type="submit"
            form="google-extra-form"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: "#f2722b",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 600,
              "&:hover": { bgcolor: "#d95f1d" },
            }}
          >
            {loading ? "Saving..." : "Complete Profile"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
